import { useState } from "react";
import { Tag, Loader2, Save, Upload } from "lucide-react";
import { api, compressImage, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { ZoomablePreview } from "@/components/app/ZoomablePreview";

const TagDecoder = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tagText, setTagText] = useState("");
  const [make, setMake] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [readMeta, setReadMeta] = useState(null);

  const processFile = async (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setReadMeta(null);
    // Auto-read the tag text and pre-fill the textarea
    setReading(true);
    try {
      const { base64, mime } = await compressImage(f, { maxDim: 1600, quality: 0.85 });
      const { data: resp } = await api.post("/vehicles/read-tag-photo", {
        image_base64: base64,
        mime_type: mime,
      });
      const read = resp.reading || {};
      if (read.tag_text) setTagText(read.tag_text);
      if (read.make_guess && !make) setMake(read.make_guess);
      setReadMeta(read);
      if (read.tag_text) toast.success("Tag text read — review & edit if needed");
      else toast.error("Could not read tag text from that photo");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setReading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) await processFile(f);
  };

  const decode = async () => {
    if (!file && !tagText.trim()) {
      toast.error("Upload a tag photo or type the codes.");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const payload = { make: make || null, tag_text: tagText || null };
      if (file) {
        const { base64, mime } = await compressImage(file, { maxDim: 1400, quality: 0.85 });
        payload.image_base64 = base64;
        payload.mime_type = mime;
      }
      const { data: resp } = await api.post("/vehicles/decode-tag", payload);
      setData(resp);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.post("/vehicles/garage", {
        make: make || (data.decoded?.make_guess ?? ""),
        tag_decoded: data.decoded,
        notes: tagText || undefined,
      });
      toast.success("Tag filed in your garage");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const d = data?.decoded || {};

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Station 03</p>
        <h1 className="font-display text-5xl uppercase text-cream mt-1">Body Tag Decoder</h1>
        <p className="text-secondary mt-2 max-w-2xl">
          Fisher Body cowl tags, Ford door plates, Mopar fender tags. Upload a photo, type the codes, or both.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-6 space-y-4">
          <span className="tag-chip">Input</span>

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary">Make (Optional)</label>
            <select
              data-testid="tag-make-select"
              className="data-input mt-1"
              value={make}
              onChange={(e) => setMake(e.target.value)}
            >
              <option value="">— Let AI guess —</option>
              <option value="Chevrolet">Chevrolet (GM Fisher Body)</option>
              <option value="Pontiac">Pontiac (GM Fisher Body)</option>
              <option value="Buick">Buick (GM Fisher Body)</option>
              <option value="Oldsmobile">Oldsmobile (GM Fisher Body)</option>
              <option value="Ford">Ford / Mercury (Door Data Plate)</option>
              <option value="Mopar">Mopar (Plymouth/Dodge/Chrysler Fender)</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                Tag Codes (Type as printed, or auto-filled from photo)
              </label>
              {reading && (
                <span className="font-mono text-[10px] uppercase text-amber-brass flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" /> Reading photo…
                </span>
              )}
            </div>
            <textarea
              data-testid="tag-text-input"
              rows={5}
              className="data-input mt-1 font-mono"
              placeholder={"Ex (Chevy cowl tag):\n69-1237 ST\n12 NOR 123456 BDY\nTR 774 76-76 PNT"}
              value={tagText}
              onChange={(e) => setTagText(e.target.value)}
              disabled={reading}
            />
            {readMeta && (readMeta.plate_type_guess || readMeta.ambiguous_chars?.length > 0) && (
              <div data-testid="tag-read-meta" className="mt-2 border-l-2 border-[var(--accent-amber)] pl-2 py-1">
                {readMeta.plate_type_guess && (
                  <div className="font-mono text-[10px] uppercase text-secondary">
                    Plate looks like: <span className="text-amber-brass">{readMeta.plate_type_guess}</span>
                  </div>
                )}
                {readMeta.ambiguous_chars?.length > 0 && (
                  <div className="font-mono text-[10px] uppercase text-secondary mt-0.5">
                    Watch these characters:{" "}
                    {readMeta.ambiguous_chars.map((c, i) => (
                      <span key={i} className="text-red-candy mr-2">{c}</span>
                    ))}
                  </div>
                )}
                {readMeta.notes && (
                  <div className="text-[10px] text-secondary mt-0.5 italic">{readMeta.notes}</div>
                )}
              </div>
            )}
          </div>

          <label className="block border-2 border-dashed border-shop-strong hover:border-[var(--accent-amber)] transition-colors cursor-pointer p-6 text-center">
            <input data-testid="tag-file-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
            {preview ? (
              <ZoomablePreview
                src={preview}
                alt="tag preview"
                testid="tag-preview"
                className="max-h-52"
                onCrop={processFile}
                cropLabel="Re-read this crop"
              />
            ) : (
              <div className="text-secondary">
                <Upload size={28} className="mx-auto text-amber-brass mb-2" />
                <p className="font-display uppercase tracking-widest text-cream">Or upload a photo of the tag</p>
              </div>
            )}
          </label>

          <button data-testid="tag-decode-btn" onClick={decode} disabled={loading} className="chrome-btn w-full flex items-center justify-center gap-2">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Decoding…</> : <><Tag size={16} /> Decode Tag</>}
          </button>
        </div>

        <div className="panel p-6 min-h-[400px]">
          <div className="flex items-center justify-between">
            <span className="tag-chip">Output</span>
            {d.confidence && <span className="font-mono text-xs uppercase text-amber-brass">Confidence: {d.confidence}</span>}
          </div>

          {!data && !loading && <div className="text-secondary text-sm mt-6">Decoded fields will appear here.</div>}
          {loading && <div className="flex items-center gap-2 mt-6 text-amber-brass font-mono text-sm"><Loader2 size={16} className="animate-spin" /> Cross-referencing option books…</div>}

          {data && (
            <div data-testid="tag-result" className="mt-4 space-y-4">
              <div>
                <div className="font-display text-2xl uppercase text-cream">{d.tag_type || "Unknown Tag Type"}</div>
                {d.make_guess && <div className="font-mono text-xs text-amber-brass mt-1">Best-guess make: {d.make_guess}</div>}
              </div>

              {d.special_variant && (
                <div className="border-l-4 border-[var(--accent-red)] bg-[rgba(217,32,39,0.08)] px-4 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-candy">Special Variant</div>
                  <div className="font-display text-2xl uppercase text-cream mt-0.5">{d.special_variant}</div>
                </div>
              )}

              {d.summary && (
                <div className="border-l-2 border-[var(--accent-red)] pl-3 text-sm text-cream">{d.summary}</div>
              )}

              {d.fields?.length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase text-amber-brass mb-2">Decoded Fields</div>
                  <div className="border border-shop">
                    <table className="w-full text-sm">
                      <thead className="bg-elevated">
                        <tr className="font-mono text-[10px] uppercase text-secondary">
                          <th className="text-left px-3 py-2">Code</th>
                          <th className="text-left px-3 py-2">Label</th>
                          <th className="text-left px-3 py-2">Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.fields.map((f, i) => (
                          <tr key={i} className="border-t border-shop align-top">
                            <td className="px-3 py-2 font-mono text-amber-brass">{f.code}</td>
                            <td className="px-3 py-2 font-mono text-cream">{f.label}</td>
                            <td className="px-3 py-2 text-cream">{f.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {d.unknown_codes?.length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase text-secondary mb-1">Unknown codes</div>
                  <div className="flex flex-wrap gap-2">
                    {d.unknown_codes.map((c, i) => <span key={i} className="tag-chip">{c}</span>)}
                  </div>
                </div>
              )}

              <button data-testid="tag-save-btn" onClick={save} disabled={saving} className="ghost-btn flex items-center gap-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save to Garage
              </button>

              {data.raw && !d.fields && (
                <pre className="text-xs bg-elevated p-3 border border-shop text-secondary whitespace-pre-wrap">{data.raw}</pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagDecoder;
